import React, { useEffect, useState } from "react";
import { Unit, UnitDimension } from "../../types/Unit";
import {
    getAllUnitDimensions,
    createUnitDimension,
    updateUnitDimension,
    deleteUnitDimension,
    getAllUnits,
} from "../../api/UnitAPI";
import { logApiError, getUserMessage, parseApiError } from "../../api/apiError";

interface Props {
    token: string;
    /** Called whenever dimensions change so sibling sections can refresh their dropdowns */
    onDimensionsChange?: (dims: UnitDimension[]) => void;
}

const UnitDimensionSection: React.FC<Props> = ({ token, onDimensionsChange }) => {
    const [items, setItems] = useState<UnitDimension[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [newName, setNewName] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const [newConvertible, setNewConvertible] = useState(true);
    const [newAllowMultiple, setNewAllowMultiple] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const [editingId, setEditingId] = useState<string | null>(null);
    const [editName, setEditName] = useState("");
    const [editDesc, setEditDesc] = useState("");
    const [editBaseUnitId, setEditBaseUnitId] = useState("");
    const [editConvertible, setEditConvertible] = useState(true);
    const [editAllowMultiple, setEditAllowMultiple] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    useEffect(() => {
        load();
    }, [token]);

    async function load() {
        setLoading(true);
        setLoadError(null);
        try {
            const [data, unitList] = await Promise.all([
                getAllUnitDimensions(token),
                getAllUnits(token),
            ]);
            setItems(data);
            setUnits(unitList);
            onDimensionsChange?.(data);
        } catch (err) {
            logApiError(err);
            setLoadError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }

    function notify(updated: UnitDimension[]) {
        setItems(updated);
        onDimensionsChange?.(updated);
    }

    async function handleCreate() {
        if (!newName.trim()) { setCreateError("Name is required."); return; }
        setCreateError(null);
        setLoading(true);
        try {
            const created = await createUnitDimension(token, {
                name: newName.trim(),
                description: newDesc.trim() || undefined,
                convertible: newConvertible,
                allowMultiplePerSystem: newAllowMultiple,
            });
            notify([...items, created]);
            setNewName("");
            setNewDesc("");
            setNewConvertible(true);
            setNewAllowMultiple(false);
        } catch (err) {
            logApiError(err);
            setCreateError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }

    function startEdit(item: UnitDimension) {
        setEditingId(item.id);
        setEditName(item.name);
        setEditDesc(item.description ?? "");
        setEditBaseUnitId(item.baseUnitId ?? "");
        setEditConvertible(item.convertible ?? true);
        setEditAllowMultiple(item.allowMultiplePerSystem ?? false);
        setEditError(null);
        setDeleteError(null);
    }

    function cancelEdit() {
        setEditingId(null);
        setEditError(null);
    }

    async function handleSaveEdit(item: UnitDimension) {
        setEditError(null);
        setLoading(true);
        try {
            const updated = await updateUnitDimension(token, {
                id: item.id,
                version: item.version,
                name: editName.trim() || undefined,
                description: editDesc.trim() || undefined,
                baseUnitId: editBaseUnitId || null,
                convertible: editConvertible,
                allowMultiplePerSystem: editAllowMultiple,
            });
            notify(items.map((x) => x.id === updated.id ? updated : x));
            setEditingId(null);
        } catch (err) {
            logApiError(err);
            // On a version conflict, refresh the latest data so the version the
            // form holds is current — otherwise every retry keeps failing. The
            // user's in-progress field edits are preserved; they just re-Save.
            if (parseApiError(err)?.errorName === "OptimisticLocking") {
                await load();
                setEditError(
                    "This dimension was changed elsewhere. The latest version has been reloaded — please review your changes and Save again."
                );
            } else {
                setEditError(getUserMessage(err));
            }
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete(item: UnitDimension) {
        setDeleteError(null);
        setLoading(true);
        try {
            await deleteUnitDimension(token, item.id, item.version);
            notify(items.filter((x) => x.id !== item.id));
            if (editingId === item.id) setEditingId(null);
        } catch (err) {
            logApiError(err);
            if (parseApiError(err)?.errorName === "OptimisticLocking") {
                await load();
                setDeleteError(
                    "This dimension was changed elsewhere. The list has been refreshed — please try again."
                );
            } else {
                setDeleteError(getUserMessage(err));
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="unit-section">
            <h3>Unit Dimensions</h3>

            {loadError && <div className="error-message">{loadError}</div>}
            {loading && <div className="status-loading">Loading…</div>}

            <ul className="unit-list">
                {items.map((item) => (
                    <li key={item.id} className="unit-row">
                        {editingId === item.id ? (
                            <div className="unit-edit-form">
                                <div className="unit-field-row">
                                    <label>Name</label>
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        disabled={loading}
                                    />
                                </div>
                                <div className="unit-field-row">
                                    <label>Description</label>
                                    <input
                                        type="text"
                                        value={editDesc}
                                        onChange={(e) => setEditDesc(e.target.value)}
                                        disabled={loading}
                                    />
                                </div>
                                <div className="unit-field-row">
                                    <label>Base unit</label>
                                    <select value={editBaseUnitId} onChange={(e) => setEditBaseUnitId(e.target.value)} disabled={loading}>
                                        <option value="">-- none --</option>
                                        {units.filter((u) => u.unitDimensionId === item.id).map((u) => (
                                            <option key={u.id} value={u.id}>{u.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: "normal" }}>
                                    <input type="checkbox" checked={editConvertible} onChange={(e) => setEditConvertible(e.target.checked)} disabled={loading} />
                                    Convertible (store values in all units; uncheck for currency)
                                </label>
                                <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: "normal" }}>
                                    <input type="checkbox" checked={editAllowMultiple} onChange={(e) => setEditAllowMultiple(e.target.checked)} disabled={loading} />
                                    Allow multiple units per system (e.g. currency)
                                </label>
                                <div className="unit-row-actions">
                                    <button onClick={() => handleSaveEdit(item)} disabled={loading}>Save</button>
                                    <button onClick={cancelEdit} disabled={loading} className="btn-secondary">Cancel</button>
                                </div>
                                {editError && <div className="error-message">{editError}</div>}
                            </div>
                        ) : (
                            <div className="unit-row-view">
                                <span className="unit-name">{item.name}</span>
                                {item.description && <span className="unit-desc">{item.description}</span>}
                                {item.baseUnitId && (
                                    <span className="unit-meta">base: {units.find((u) => u.id === item.baseUnitId)?.name ?? item.baseUnitId}</span>
                                )}
                                <div className="unit-row-actions" style={{ marginLeft: "auto" }}>
                                    <button onClick={() => startEdit(item)} disabled={loading || !!editingId}>Edit</button>
                                    <button onClick={() => handleDelete(item)} disabled={loading || !!editingId}>Delete</button>
                                </div>
                            </div>
                        )}
                    </li>
                ))}
            </ul>

            {deleteError && <div className="error-message">{deleteError}</div>}

            {items.length === 0 && !loading && !loadError && (
                <p style={{ color: "var(--text-muted)" }}>No unit dimensions yet.</p>
            )}

            <div className="unit-create-form">
                <h4>Add Unit Dimension</h4>
                <div className="unit-field-row">
                    <label>Name</label>
                    <input
                        type="text"
                        value={newName}
                        onChange={(e) => { setNewName(e.target.value); setCreateError(null); }}
                        placeholder="e.g. Length"
                        disabled={loading}
                    />
                </div>
                <div className="unit-field-row">
                    <label>Description</label>
                    <input
                        type="text"
                        value={newDesc}
                        onChange={(e) => setNewDesc(e.target.value)}
                        placeholder="Optional description"
                        disabled={loading}
                    />
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: "normal" }}>
                    <input type="checkbox" checked={newConvertible} onChange={(e) => setNewConvertible(e.target.checked)} disabled={loading} />
                    Convertible (store values in all units; uncheck for currency)
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: "normal" }}>
                    <input type="checkbox" checked={newAllowMultiple} onChange={(e) => setNewAllowMultiple(e.target.checked)} disabled={loading} />
                    Allow multiple units per system (e.g. currency)
                </label>
                <button onClick={handleCreate} disabled={loading || !!editingId}>Add</button>
                {createError && <div className="error-message">{createError}</div>}
            </div>
        </div>
    );
};

export default UnitDimensionSection;
